// client/src/components/pages/RouteSummary/index.tsx (converted from .js, ST-00216)
//
// Route Summary page: the filter bar, the generated summary table and the
// Recon view of the same routes and period.
import { useState, useEffect, useRef, type ComponentType } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { FaChartBar, FaRoute, FaTruck, FaWarehouse } from 'react-icons/fa';
import { HiOutlineDocumentReport } from 'react-icons/hi';
import api from '../../../services/api';
import SummaryTable from './SummaryTable';
import FilterControlsJs from './FilterControls';
import ReconView from './ReconView';
import LoadMatchingPreview from './LoadMatchingPreview';
import LocationSuggestionsIndicator from './LocationSuggestionsIndicator';
import LexiBridgeLoaderJs from '../../common/LexiBridgeLoader';
import { PageHeader } from '../../../types/commonComponents';
import { defaultRouteSummaryPeriod, snapshotSummaryParams } from './routeSummaryParams';
import type { GroupingId, RouteSummaryResponse } from '../../../types/clientPages/routeSummary';
import type {
  GeneratedSummaryParams,
  RouteGrouping,
  RouteSummaryApiError,
} from '../../../types/clientPages/routeSummaryRecon';

// Typed views of the JS modules.
const FilterControls = FilterControlsJs as unknown as ComponentType<{
  startDate: string;
  setStartDate: (date: string) => void;
  endDate: string;
  setEndDate: (date: string) => void;
  minWeight: number;
  setMinWeight: (weight: number) => void;
  selectedGroupings: RouteGrouping[];
  setSelectedGroupings: (groupings: RouteGrouping[]) => void;
  groupings: RouteGrouping[];
  onGenerate: () => void;
  loading: boolean;
  loadingGroupings: boolean;
  hasResults: boolean;
}>;
const LexiBridgeLoader = LexiBridgeLoaderJs as unknown as ComponentType<{
  message?: string;
  compact?: boolean;
}>;

type ResultsTab = 'summary' | 'recon';

/** Router state Operations Overview passes when it opens a route here. */
interface RouteSummaryNavState {
  fromOperationsOverview?: boolean;
  groupingName?: string;
}

// Quiet teaching empty state (ST-00124): explains the page and previews the
// current selection — no hero banner, no decorative motion. The filter bar
// above is the single call to action.
const SelectionPreview = ({ selectedGroupings }: { selectedGroupings: RouteGrouping[] }) => {
  if (selectedGroupings.length === 1) {
    const g = selectedGroupings[0] as RouteGrouping;
    return (
      <div className="mt-6 mx-auto max-w-lg text-left bg-slate-50 rounded-lg border border-slate-200 divide-y divide-slate-200">
        {g.description && (
          <p className="px-4 py-3 text-sm text-slate-600 italic">{g.description}</p>
        )}
        <div className="px-4 py-3 flex gap-3">
          <FaTruck className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-700 mb-0.5">Dispatch</p>
            <p className="text-sm text-slate-600 break-words">{g.dispatchLocations.join(', ')}</p>
          </div>
        </div>
        <div className="px-4 py-3 flex gap-3">
          <FaWarehouse className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-700 mb-0.5">Receipt</p>
            <p className="text-sm text-slate-600 break-words">{g.receiptLocations.join(', ')}</p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="mt-6 mx-auto max-w-lg text-left bg-slate-50 rounded-lg border border-slate-200 px-4 py-3">
      <p className="text-xs font-semibold text-slate-700 mb-2">
        {selectedGroupings.length} routes will be combined
      </p>
      <ul className="space-y-1.5">
        {selectedGroupings.map((g) => (
          <li key={g.id} className="text-sm text-slate-600 flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0 mt-1.5"></span>
            <span className="break-words">{g.name}</span>
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-slate-500 mt-2.5">
        Metrics are summed across all selected routes; shared locations are counted once.
      </p>
    </div>
  );
};

const EmptyState = ({ selectedGroupings }: { selectedGroupings: RouteGrouping[] }) => {
  const hasSelection = selectedGroupings.length > 0;
  return (
    <div className="bg-surface rounded-xl border border-slate-200 shadow-sm px-6 py-12 text-center">
      <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center mx-auto mb-4">
        <HiOutlineDocumentReport className="w-6 h-6 text-primary-600" />
      </div>
      <h3 className="text-base font-semibold text-slate-900 mb-1">
        {hasSelection ? 'Ready when you are' : 'Compare dispatch and receipt records'}
      </h3>
      <p className="text-sm text-slate-500 max-w-md mx-auto">
        {hasSelection
          ? 'Generate the summary above to analyse the selected route' +
            (selectedGroupings.length > 1 ? 's' : '') +
            ' over your chosen period.'
          : 'Pick one or more routes in the bar above. You’ll get a daily breakdown of dispatch vs receipt weights, counts, and variances.'}
      </p>
      {hasSelection && <SelectionPreview selectedGroupings={selectedGroupings} />}
    </div>
  );
};

// Progress indicator component - Modern, refined design
const ProgressIndicator = ({
  message,
  progress,
  onCancel,
}: {
  message: string;
  progress?: number;
  onCancel?: () => void;
}) => (
  <div className="bg-surface border border-slate-200 rounded-xl shadow-sm overflow-hidden">
    {/* Progress bar at top */}
    <div className="h-1 bg-slate-100">
      <div
        className="h-full bg-gradient-to-r from-primary-400 to-primary-600 transition-all duration-500 ease-out"
        style={{ width: `${progress || 0}%` }}
      ></div>
    </div>

    <div className="p-6">
      <div className="flex items-center gap-4">
        <div className="relative flex-shrink-0">
          <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center">
            <svg
              className="animate-spin h-6 w-6 text-primary-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900 mb-1">{message}</p>
          {progress !== undefined && <p className="text-xs text-slate-500">{progress}% complete</p>}
        </div>
        {onCancel && (
          <button
            onClick={onCancel}
            className="flex-shrink-0 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  </div>
);

// Slim strip between the filter bar and the results: route context on the
// left, Summary/Recon tabs on the right. Filters live permanently in the bar
// above, so there is no toggle here (ST-00124).
const ResultsStrip = ({
  groupingLabel,
  groupingDescription,
  activeTab,
  setActiveTab,
}: {
  groupingLabel: string | null;
  groupingDescription: string | null | undefined;
  activeTab: ResultsTab;
  setActiveTab: (tab: ResultsTab) => void;
}) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-1">
    <div className="flex items-center gap-2 min-w-0">
      <FaRoute className="text-primary-600 text-xs flex-shrink-0" />
      <p className="text-sm font-semibold text-slate-900 truncate">{groupingLabel}</p>
      {groupingDescription && (
        <p className="text-xs text-slate-500 italic truncate hidden md:block">
          {groupingDescription}
        </p>
      )}
    </div>
    <div className="flex items-center gap-1 bg-slate-100 p-0.5 rounded-lg self-start sm:self-auto">
      {(['summary', 'recon'] as const).map((tab) => (
        <button
          key={tab}
          onClick={() => setActiveTab(tab)}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-150 capitalize ${
            activeTab === tab
              ? 'bg-surface text-slate-900 shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          {tab}
        </button>
      ))}
    </div>
  </div>
);

const RouteSummary = () => {
  // URL parameters for deep linking from Operations Overview
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navState = location.state as RouteSummaryNavState | null | undefined;

  // Check if we came from Operations Overview
  const fromOperationsOverview = navState?.fromOperationsOverview;
  const groupingNameFromNav = navState?.groupingName;

  // State for filters: URL params first (deep link from Operations Overview),
  // otherwise the 1st of the month to yesterday — or, on the 1st, the whole
  // previous month (ST-00215, RSO-20).
  const [startDate, setStartDate] = useState(
    () => searchParams.get('startDate') || defaultRouteSummaryPeriod().startDate
  );

  const [endDate, setEndDate] = useState(
    () => searchParams.get('endDate') || defaultRouteSummaryPeriod().endDate
  );

  const [minWeight, setMinWeight] = useState(5);
  // Multi-select: array of selected grouping objects. A single selection behaves
  // exactly like the legacy single-grouping flow (server routes it to the legacy
  // path); multiple selections produce one combined, overlap-safe summary.
  const [selectedGroupings, setSelectedGroupings] = useState<RouteGrouping[]>([]);
  const [groupings, setGroupings] = useState<RouteGrouping[]>([]);

  // The "primary" grouping drives single-grouping contexts that stay single in
  // this pilot (Recon view, per-day drill-down modals, deep-link).
  const primaryGrouping = selectedGroupings[0] || null;
  const combinedName =
    selectedGroupings.length === 0
      ? null
      : selectedGroupings.length === 1
        ? (selectedGroupings[0] as RouteGrouping).name
        : `${selectedGroupings.length} routes combined: ${selectedGroupings.map((g) => g.name).join(' + ')}`;

  // State for summary data
  const [summaryData, setSummaryData] = useState<RouteSummaryResponse | null>(null);
  // ST-00215 (RSO-10): the routes, period and Min weight the summary on screen
  // was generated with. Drill-downs, Recon and the SQL Query use these, not the
  // live filter bar.
  const [generatedParams, setGeneratedParams] = useState<GeneratedSummaryParams | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingGroupings, setLoadingGroupings] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  // Unified loading state for seamless navigation from Operations Overview
  const [showUnifiedLoader, setShowUnifiedLoader] = useState(fromOperationsOverview);

  // Tab state for switching between Summary and Recon View
  const [activeTab, setActiveTab] = useState<ResultsTab>('summary'); // 'summary' or 'recon'

  // Trigger for Recon View to load data (incremented when Generate Summary is clicked)
  const [reconViewTrigger, setReconViewTrigger] = useState(0);

  // Refs for request cancellation
  const abortControllerRef = useRef<AbortController | null>(null);
  const groupingsAbortControllerRef = useRef<AbortController | null>(null);
  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Guard ref to prevent auto-generate from firing more than once
  const autoGenerateTriggeredRef = useRef(false);

  // Fetch groupings only on mount (not when dates change)
  // This prevents unwanted page refreshes when changing dates
  useEffect(() => {
    // Fetch groupings once on component mount with initial date values
    fetchGroupings(startDate, endDate);

    // Cleanup on unmount
    return () => {
      if (groupingsAbortControllerRef.current) {
        groupingsAbortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  // Auto-select grouping from URL parameter after groupings are loaded
  useEffect(() => {
    const groupingIdParam = searchParams.get('groupingId');

    if (groupingIdParam && groupings.length > 0 && selectedGroupings.length === 0) {
      const groupingId = parseInt(groupingIdParam);
      const grouping = groupings.find((g) => g.id === groupingId);

      if (grouping) {
        setSelectedGroupings([grouping]);
        console.log(`Auto-selected grouping from URL: ${grouping.name} (ID: ${groupingId})`);
      } else {
        console.warn(`Grouping with ID ${groupingId} not found in available groupings`);
        // ST-00215 (RSO-13): opened from Operations Overview, the loader used to
        // wait for a summary that could never come.
        if (showUnifiedLoader) {
          setError('That route could not be found. Pick a route above.');
          setShowUnifiedLoader(false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupings, selectedGroupings]);

  // Auto-generate summary when grouping is selected from URL (fires once only)
  useEffect(() => {
    // Guard: only auto-generate once per mount
    if (autoGenerateTriggeredRef.current) return;

    const groupingIdParam = searchParams.get('groupingId');

    // Only auto-generate if:
    // 1. We have a groupingId in URL (came from Operations Overview)
    // 2. A grouping is now selected
    // 3. We don't already have summary data
    // 4. We're not currently loading
    if (groupingIdParam && primaryGrouping && !summaryData && !loading) {
      autoGenerateTriggeredRef.current = true;
      console.log(`Auto-generating summary for: ${primaryGrouping.name}`);
      // Use setTimeout to ensure the component is fully mounted
      setTimeout(() => {
        handleGenerateSummary();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryGrouping, summaryData, loading]);

  // Hide unified loader when summary data is ready
  useEffect(() => {
    if (summaryData && showUnifiedLoader) {
      // Small delay to ensure smooth transition
      setTimeout(() => {
        setShowUnifiedLoader(false);
      }, 300);
    }
  }, [summaryData, showUnifiedLoader]);

  // ST-00215 (RSO-13): a failed load (timeout, 404, 5xx, routes not loading)
  // shows its error and the filter bar instead of an endless loader.
  useEffect(() => {
    if (error && showUnifiedLoader) {
      setShowUnifiedLoader(false);
    }
  }, [error, showUnifiedLoader]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (groupingsAbortControllerRef.current) {
        groupingsAbortControllerRef.current.abort();
      }
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  const fetchGroupings = async (
    start: string = startDate,
    end: string = endDate,
    includeTotals: boolean = false
  ) => {
    // Cancel previous request
    if (groupingsAbortControllerRef.current) {
      groupingsAbortControllerRef.current.abort();
    }

    // Clear any pending error timeout from previous fetch
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }

    // Create new abort controller
    groupingsAbortControllerRef.current = new AbortController();

    setLoadingGroupings(true);
    // Clear any existing error when starting a new fetch
    setError(null);

    try {
      // PERFORMANCE FIX: Only fetch totals when explicitly requested
      // This makes route selection instant (fast path) vs 2-5 seconds (slow path with totals)
      const response = await api.routeSummary.getGroupings(start, end, {
        signal: groupingsAbortControllerRef.current.signal,
        includeTotals, // Pass the includeTotals flag to backend
      });

      setGroupings(response.data as RouteGrouping[]);
    } catch (caught) {
      const err = caught as RouteSummaryApiError;
      if (err.name !== 'AbortError') {
        console.error('Error fetching groupings:', err);

        // Only show error after a short delay to prevent flashing
        // Store timeout ref so it can be cancelled if needed
        errorTimeoutRef.current = setTimeout(() => {
          // Only set error if groupings are still empty (prevents stale errors)
          setGroupings((currentGroupings) => {
            if (currentGroupings.length === 0) {
              setError('Failed to load groupings');
            }
            return currentGroupings;
          });
          errorTimeoutRef.current = null;
        }, 500);
      }
    } finally {
      setLoadingGroupings(false);
    }
  };

  const handleGenerateSummary = async () => {
    if (selectedGroupings.length === 0) {
      setError('Please select a grouping');
      return;
    }

    // The unified filter bar stays in place on all viewports (ST-00124) —
    // no collapse choreography before generating.

    // Snapshot what is being generated; set in the same render as the Recon
    // trigger below so Recon loads with exactly these parameters.
    const params = snapshotSummaryParams({ selectedGroupings, startDate, endDate, minWeight });
    setGeneratedParams({
      ...params,
      label: combinedName,
      description: selectedGroupings.length === 1 ? primaryGrouping?.description : null,
    });

    // Trigger Recon View to load data as well
    setReconViewTrigger((prev) => prev + 1);

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);
    setProgress(0);
    setProgressMessage('Initializing summary generation...');

    let progressInterval: ReturnType<typeof setInterval> | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
      // Improved progress simulation that doesn't get stuck at 90%
      let currentProgress = 0;
      progressInterval = setInterval(() => {
        setProgress((prev) => {
          currentProgress = prev;
          if (prev < 85) {
            // Fast progress to 85%
            return prev + 15;
          } else if (prev < 95) {
            // Slower progress from 85% to 95%
            return prev + 2;
          } else if (prev < 98) {
            // Very slow progress from 95% to 98%
            return prev + 0.5;
          }
          // Stay at 98% until API responds
          return prev;
        });
      }, 300);

      // Set a timeout to prevent infinite waiting
      timeoutId = setTimeout(() => {
        if (currentProgress < 100) {
          setProgressMessage('Request is taking longer than expected...');
        }
      }, 30000); // 30 seconds

      setProgressMessage('Fetching grouping data...');

      const response = await api.routeSummary.generateSummary(
        {
          // New multi-select shape. A single-element array is routed to the exact
          // legacy code path server-side, so single selection stays byte-identical.
          groupingIds: params.groupingIds,
          startDate: params.startDate,
          endDate: params.endDate,
          minWeight: params.minWeight,
        },
        {
          signal: abortControllerRef.current.signal,
        }
      );

      // Clear intervals and complete progress
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      setProgress(100);
      setProgressMessage('Summary generated successfully!');

      // Set summary data immediately to prevent race condition with auto-generate effect.
      // The loading state is cleared in the finally block, so summaryData must be set
      // before that to avoid the auto-generate effect re-triggering.
      setSummaryData(response.data as RouteSummaryResponse);

      // Brief delay to let the 100% progress bar render, then clear progress indicators
      setTimeout(() => {
        setProgress(0);
        setProgressMessage('');
      }, 500);
    } catch (caught) {
      const err = caught as RouteSummaryApiError;
      // Clean up intervals on error
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      if (err.name !== 'AbortError') {
        console.error('Error generating summary:', err);

        // Provide more specific error messages
        let errorMessage = 'Failed to generate summary';
        if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
          errorMessage =
            'Request timed out. Please try again or contact support if the issue persists.';
        } else if (err.response?.status === 404) {
          errorMessage = 'Grouping not found. Please refresh the page and try again.';
        } else if ((err.response?.status ?? 0) >= 500) {
          errorMessage = 'Server error occurred. Please try again in a few moments.';
        } else if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        }

        setError(errorMessage);
        setSummaryData(null);
      }

      setProgress(0);
      setProgressMessage('');
    } finally {
      setLoading(false);
    }
  };

  // Cancel summary generation
  const handleCancelGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setLoading(false);
    setProgress(0);
    setProgressMessage('');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={FaChartBar}
        title="Route Summary"
        subtitle="Generate comprehensive summaries based on location groupings"
      />

      {/* Unified LexiBridge Loader - Shows during navigation from Operations Overview */}
      {showUnifiedLoader && (
        <div className="flex items-center justify-center min-h-[80vh] bg-gray-50">
          <LexiBridgeLoader
            message={groupingNameFromNav ? `Loading ${groupingNameFromNav}` : 'Generating summary'}
          />
        </div>
      )}

      {/* Main Content Area */}
      {!showUnifiedLoader && (
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          {/* Unified layout (ST-00124): one filter bar, always on top, never morphs */}
          <div className="space-y-3">
            <FilterControls
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              minWeight={minWeight}
              setMinWeight={setMinWeight}
              selectedGroupings={selectedGroupings}
              setSelectedGroupings={setSelectedGroupings}
              groupings={groupings}
              onGenerate={handleGenerateSummary}
              loading={loading}
              loadingGroupings={loadingGroupings}
              hasResults={!!summaryData}
            />

            {error && (
              <div className="bg-red-50 border border-red-200 p-4 rounded-xl flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <svg
                    className="w-5 h-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-900">{error}</p>
                </div>
                <button
                  onClick={() => setError(null)}
                  className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            )}

            {loading && progressMessage && (
              <ProgressIndicator
                message={progressMessage}
                progress={progress}
                onCancel={handleCancelGeneration}
              />
            )}

            {!loading && !summaryData && <EmptyState selectedGroupings={selectedGroupings} />}

            {!loading && summaryData && generatedParams && (
              <>
                <ResultsStrip
                  groupingLabel={generatedParams.label}
                  groupingDescription={generatedParams.description}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                />

                {activeTab === 'summary' && (
                  <div className="bg-surface rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                    <SummaryTable
                      data={summaryData.summary}
                      preload={summaryData.preload}
                      carriedForward={summaryData.carriedForward}
                      // ST-00182: per-day matched variance block. An older
                      // server (or a stale cache entry) answers without it —
                      // treat that as "matching off" so the table shows the
                      // explanatory note instead of calendar arithmetic.
                      matchedVariance={
                        summaryData.matchedVariance || { available: false, reason: 'none' }
                      }
                      grouping={{
                        ...summaryData.grouping,
                        name: generatedParams.label,
                      }}
                      // ST-00215 (RSO-6/RSO-10): every route of the summary and
                      // the parameters it was generated with.
                      groupingId={generatedParams.groupingIds[0]}
                      groupingIds={generatedParams.groupingIds}
                      startDate={generatedParams.startDate}
                      endDate={generatedParams.endDate}
                      minWeight={generatedParams.minWeight}
                      // ST-00218 P3: how the figures were matched (the fallback note) and the
                      // period figures (Truck + time: the adjusted bottom line and its rows).
                      matching={summaryData.matching ?? null}
                      figures={summaryData.figures ?? null}
                      // ST-00218 P3: tickets probably keyed with the wrong location (asked
                      // after the summary loads; nothing while there is none).
                      locationNotice={
                        <LocationSuggestionsIndicator
                          groupingIds={generatedParams.groupingIds}
                          startDate={generatedParams.startDate}
                          endDate={generatedParams.endDate}
                          label={generatedParams.label}
                        />
                      }
                      // ST-00218 P2: read-only "Preview new matching" (load_matching.view) —
                      // not once the route's figures come from the new matching (P3).
                      bfcfPreview={
                        summaryData.matching?.effective === 'engine' ? null : (
                          <LoadMatchingPreview
                            groupingIds={generatedParams.groupingIds.map(Number)}
                            startDate={generatedParams.startDate}
                            endDate={generatedParams.endDate}
                            minWeight={generatedParams.minWeight}
                          />
                        )
                      }
                    />
                  </div>
                )}

                {activeTab === 'recon' && (
                  <ReconView
                    // A summary is only generated with at least one route selected.
                    groupingId={generatedParams.groupingIds[0] as GroupingId}
                    groupingIds={generatedParams.groupingIds}
                    groupingName={generatedParams.groupingName}
                    startDate={generatedParams.startDate}
                    endDate={generatedParams.endDate}
                    minWeight={generatedParams.minWeight}
                    triggerLoad={reconViewTrigger}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteSummary;
